import { debounce } from '@fe/core';
import { BarbColorType, WeatherSettingsChangeSource } from '@fe/weather';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Checkbox, ColorPickerChangeEvent, RadioGroup, SliderChangeEvent } from '@progress/kendo-react-inputs';
import React from 'react';
import { ColorPickerWithDefault } from '../../../../app/components/inputs/ColorPickerWithDefault';
import { SliderWithTooltip } from '../../../../app/components/inputs/SliderWithTooltip';
import SettingsSubSection from '../../../common/SettingsSubSection';
import useWeatherService from '../../../hooks/use-weather-service';
import {
    BarbColorTypeOptions,
    BarbSizeOptions,
    JetStreamWidthOptions,
    ProbSeverePrimaryDisplayOptions,
    ProbSevereSecondaryDisplayOptions,
    ProbSeverMinimumLevelOptions,
    WindDepictionOptions,
    WindHeatmapOptions,
    WindParticleColorOptions,
    WindTempOptions
} from '../settings-options';
import { useBooleanSetting, useDropdownSetting, useRadioGroupSetting } from './hooks/useSetting';

export default function WindsSettings() {
    const weatherService = useWeatherService();
    const changeSource = WeatherSettingsChangeSource.winds;
    const windDepiction = useRadioGroupSetting(WindDepictionOptions, 'winds', 'windDepiction', WeatherSettingsChangeSource.windsDepiction);
    const heatmapStyle = useRadioGroupSetting(WindHeatmapOptions, 'winds', 'heatmapStyle', WeatherSettingsChangeSource.windsHeatmapStyle);
    const [heatmapTransparency, setHeatmapTransparency] = React.useState<number>(100);
    const particleColor = useRadioGroupSetting(WindParticleColorOptions, 'winds', 'particleColor', changeSource);
    const [particleTransparency, setParticleTransparency] = React.useState<number>(0.2);
    const barbColorType = useRadioGroupSetting(BarbColorTypeOptions, 'winds', 'barbColorType', WeatherSettingsChangeSource.windsBarbsColor);
    const [barbColor, setBarbColor] = React.useState<string>('#fff');
    const jetStreamWidth = useDropdownSetting(JetStreamWidthOptions, 'winds', 'jetStreamWidth', WeatherSettingsChangeSource.jetStreamWidth);
    const windTempMode = useDropdownSetting(WindTempOptions, 'winds', 'windTemp', WeatherSettingsChangeSource.windsTemp);
    const barbSize = useDropdownSetting(BarbSizeOptions, 'winds', 'barbSize', WeatherSettingsChangeSource.windsBarbSize);
    const probSeverePrimaryDisplayType = useDropdownSetting(
        ProbSeverePrimaryDisplayOptions,
        'winds',
        'probSeverePrimaryDisplay',
        WeatherSettingsChangeSource.probSevere
    );
    const probSevereMinPrimary = useDropdownSetting(
        ProbSeverMinimumLevelOptions,
        'winds',
        'probSeverePrimaryMinLevel',
        WeatherSettingsChangeSource.probSevere
    );
    const probSevereSecondaryDisplayType = useDropdownSetting(
        ProbSevereSecondaryDisplayOptions,
        'winds',
        'probSevereSecondaryDisplay',
        WeatherSettingsChangeSource.probSevere
    );
    const probSevereMinSecondary = useDropdownSetting(
        ProbSeverMinimumLevelOptions,
        'winds',
        'probSevereSecondaryMinLevel',
        WeatherSettingsChangeSource.probSevere
    );
    const probSevereShowArrow = useBooleanSetting('winds', 'probSevereShowArrow', WeatherSettingsChangeSource.probSevere);
    const probSevereShowTrail = useBooleanSetting('winds', 'probSevereShowTrail', WeatherSettingsChangeSource.probSevere);

    React.useEffect(() => {
        setHeatmapTransparency(weatherService.getSettings().winds.heatmapOpacity * 100);
        setBarbColor(weatherService.getSettings().winds.barbColor ?? '#fff');
        setParticleTransparency(weatherService.getSettings().winds.particleOpacity);
    }, [weatherService]);

    const handleHeatmapTransparency = React.useCallback(
        (e: SliderChangeEvent) => {
            setHeatmapTransparency(e.value);
            weatherService?.updateSettings((settings) => {
                settings.winds.heatmapOpacity = e.value / 100;
            }, WeatherSettingsChangeSource.windsHeatmapTransparency);
        },
        [weatherService]
    );

    const handleParticleTransparency = React.useCallback(
        (e: SliderChangeEvent) => {
            setParticleTransparency(e.value);
            weatherService?.updateSettings((settings) => {
                settings.winds.particleOpacity = e.value;
            }, WeatherSettingsChangeSource.winds);
        },
        [weatherService]
    );

    const debounceColorChange = React.useMemo(
        () =>
            debounce<[string]>((color) => {
                weatherService?.updateSettings((settings) => {
                    settings.winds.barbColor = color;
                }, WeatherSettingsChangeSource.windsBarbsColor);
            }, 500),
        [weatherService]
    );

    const handleBarbsColor = React.useCallback(
        (e: ColorPickerChangeEvent) => {
            setBarbColor(e.value);
            debounceColorChange(e.value);
        },
        [debounceColorChange]
    );

    return (
        <div className="d-flex flex-column gap-2">
            <SettingsSubSection title="Winds">
                <div>
                    <div>Wind depiction</div>
                    <RadioGroup layout="horizontal" {...windDepiction} />
                </div>

                <div>
                    <div>Heatmap style</div>
                    <RadioGroup layout="horizontal" {...heatmapStyle} />
                </div>

                <div>
                    <div>Heatmap transparency</div>
                    <SliderWithTooltip
                        min={0}
                        max={100}
                        className="w-100"
                        value={heatmapTransparency}
                        onChange={handleHeatmapTransparency}
                    />
                </div>

                <div>
                    <div>Particle color</div>
                    <RadioGroup layout="horizontal" {...particleColor} />
                </div>
                <div>
                    <div>Particle transparency</div>
                    <SliderWithTooltip
                        min={0}
                        max={0.4}
                        className="w-100"
                        value={particleTransparency}
                        onChange={handleParticleTransparency}
                    />
                </div>
                <div>
                    <div>Winds / Temperature</div>
                    <DropDownList {...windTempMode} />
                </div>
                <div>
                    <div>Barbs color</div>
                    <RadioGroup layout="horizontal" {...barbColorType} />
                </div>

                <div className="d-flex align-items-center gap-2">
                    <div>Color:</div>
                    <ColorPickerWithDefault
                        value={barbColor}
                        views={['palette', 'gradient']}
                        defaultColor="#fff"
                        onChange={handleBarbsColor}
                        disabled={barbColorType.value === BarbColorType.intensity}
                    />
                </div>
                <div>
                    <div>Barbs size</div>
                    <DropDownList {...barbSize} />
                </div>
                <div>
                    <div>Jet stream width</div>
                    <DropDownList {...jetStreamWidth} />
                </div>
            </SettingsSubSection>

            <SettingsSubSection title="Severe Weather probability">
                <div>Primary display</div>
                <DropDownList {...probSeverePrimaryDisplayType} />
                <div>Primary minimum level to display</div>
                <DropDownList {...probSevereMinPrimary} />

                <div>Secondary display</div>
                <DropDownList {...probSevereSecondaryDisplayType} />
                <div>Secondary minimum level to display</div>
                <DropDownList {...probSevereMinSecondary} />

                <Checkbox {...probSevereShowArrow} label="Speed/direction arrow" />
                <Checkbox {...probSevereShowTrail} label="History trail" />
            </SettingsSubSection>
        </div>
    );
}
